<template lang="pug">
#users
  spinner(v-if="settings.loading")
  div(v-else)
    .page-controls
      b-field(grouped)
        b-field(expanded)
          search-field(
            :value="filters.query"
            @change="setQuery"
            @reset="resetQuery"
          )
        p.control
          b-select(
            v-model="filters.status"
            @input="getPage(1)"
          )
            option(value="all") All Users
            option(value="active") Active
            option(value="inactive") Inactive
        b-field
          button.button.is-success(@click="triggerNew")
            b-icon(icon="plus")
            span New User

    b-table(
      :data="users"
      :hoverable="true"
      :backend-pagination="true"
      :total="pagination.total"
      :current-page="pagination.page"
      :per-page="pagination.per"
      @click="triggerEdit"
      @page-change="getPage"
      paginated
    )
      template(slot-scope="props")
        b-table-column(label="Name")
          .user-name
            avatar(:username="props.row.full_name" :size="38")
            a {{ props.row.full_name }}
        b-table-column(label="Email")
          small(v-if="props.row.email") {{ props.row.email }}
          small.has-tex-grey-light(v-else) NA
        b-table-column(label="Password")
          reveal-field(
            field="password"
            resource="users"
            :value="props.row"
            @click.native.stop=""
          )
        b-table-column(label="Status")
          b-tag(:type="props.row.is_active ? 'is-success' : 'is-danger'") {{ props.row.is_active ? 'Active' : 'Inactive' }}
        b-table-column(label="Permissions")
          .permissions
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.account }"
              icon="settings"
              size="is-small"
            )
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.media }"
              icon="podcast"
              size="is-small"
            )
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.events }"
              icon="calendar-multiple"
              size="is-small"
            )
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.users }"
              icon="account-check"
              size="is-small"
            )
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.church }"
              icon="church"
              size="is-small"
            )
            b-icon(
              :class="{ 'has-text-grey-lighter': !props.row.access.articles }"
              icon="fountain-pen-tip"
              size="is-small"
            )
        b-table-column(label="Created")
          small {{ props.row.created_at | date }}
        b-table-column(label="Last Active")
          small {{ props.row.last_login | from-now('Never logged in') }}
</template>

<script>
import { mapGetters } from 'vuex';
import Avatar from 'vue-avatar';
import RevealField from '@/components/RevealField';
import SearchField from '@/components/form/SearchField';
import Spinner from '@/components/Spinner';
import UserForm from '@/components/form/UserForm';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'users',
  components: {
    Avatar,
    RevealField,
    SearchField,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
      users: getters.GET_USERS,
      refresh: getters.GET_USERS_REFRESH,
    }),
    esQuery() {
      const from = this.pagination.per * (this.pagination.page - 1);
      const payload = {
        from,
        size: this.pagination.per,
        sort: [
          { created_at: 'desc' },
        ],
        query: {
          bool: {
            must: [
              { terms: { account_ids: [this.account.id] } },
            ],
          },
        },
      };
      switch (this.filters.status) {
        case 'active':
          payload.query.bool.must.push({ match: { is_active: true } });
          break;
        case 'inactive':
          payload.query.bool.must.push({ match: { is_active: false } });
          break;
        default:
      }
      if (this.filters.query) {
        payload.query.bool.must.push({
          match_phrase_prefix: { full_name: this.filters.query },
        });
      }
      return payload;
    },
  },
  created() {
    this.getPage(1);
  },
  data() {
    return {
      filters: {
        status: 'active',
        query: null,
      },
      pagination: {
        page: 1,
        per: 10,
        total: 0,
      },
      settings: {
        loading: false,
      },
    };
  },
  methods: {
    async getPage(page) {
      this.settings.loading = true;
      this.pagination.page = page;
      await this.$store.dispatch(actions.DO_SEARCH_USERS, this.esQuery);
      this.$store.commit(mutations.SET_USERS_REFRESH, false);
      this.settings.loading = false;
    },
    open() {
      this.$buefy.modal.open({
        parent: this,
        component: UserForm,
        hasModalCard: true,
      });
    },
    resetQuery() {
      this.filters.query = null;
      this.getPage(1);
    },
    setQuery(val) {
      this.filters.query = val;
      this.getPage(1);
    },
    async triggerEdit(user) {
      await this.$store.dispatch(actions.DO_GET_USER, {
        id: user.id,
      });
      this.open();
    },
    triggerNew() {
      this.$store.commit(mutations.UNSET_USER);
      this.open();
    },
    async set($event, obj, key) {
      const payload = { id: obj.id };
      payload[key] = !obj[key];
      const response = await this.$store.dispatch(actions.DO_UPDATE_USER, payload);
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: response.message,
      });
    },
  },
  watch: {
    refresh(val) {
      if (val) this.getPage(1);
    },
  },
};
</script>

<style lang="scss">
#users {
  .b-table {
    table.table {
      tr {
        cursor: pointer;
      }
    }
  }
  .user-name {
    display: flex;
    align-items: center;
    a {
      padding-left: 1rem;
      font-weight: 500;
    }
  }
  .permissions {
    .icon + .icon {
      margin-left: 0.35rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Users.vue